import { useState, useEffect, useCallback, useMemo } from "react";
import {
  CatalogSettingDto,
  SettingDefinitionDto,
  UpdateSettingValueDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../utils/api";
import { useAuthHeaders } from "./useAuthHeaders";

export const useMyAdminSettings = () => {
  const authHeaders = useAuthHeaders();
  const [catalog, setCatalog] = useState<CatalogSettingDto[]>([]);
  const [myOverrides, setMyOverrides] = useState<SettingDefinitionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMySettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const [catalogResult, overridesResult]: [
        CatalogSettingDto[],
        SettingDefinitionDto[],
      ] = await Promise.all([
        adminApiService.gfAdminUserSettingsControllerGetCatalog(
          undefined,
          authHeaders,
        ),
        adminApiService.gfAdminUserSettingsControllerGetAdminUserSettings(
          undefined,
          authHeaders,
        ),
      ]);
      setCatalog(catalogResult);
      setMyOverrides(overridesResult);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchMySettings();
  }, []);

  const availableSettings = useMemo(
    () =>
      catalog.filter(
        (item) =>
          !myOverrides.some(
            (o) =>
              o.setting.category === item.category &&
              o.setting.settingKey === item.settingKey,
          ),
      ),
    [catalog, myOverrides],
  );

  const categories = useMemo(
    () => Array.from(new Set(catalog.map((s) => String(s.category)))),
    [catalog],
  );

  const setMyValue = useCallback(
    async (category: string, settingKey: string, value: unknown) => {
      const dto: UpdateSettingValueDto = { value };
      await adminApiService.gfAdminUserSettingsControllerSetAdminUserSetting(
        dto,
        category,
        settingKey,
        authHeaders,
      );
      await fetchMySettings();
    },
    [authHeaders, fetchMySettings],
  );

  const resetMyValue = useCallback(
    async (category: string, settingKey: string) => {
      await adminApiService.gfAdminUserSettingsControllerRemoveAdminUserSetting(
        category,
        settingKey,
        authHeaders,
      );
      await fetchMySettings();
    },
    [authHeaders, fetchMySettings],
  );

  return {
    catalog,
    myOverrides,
    availableSettings,
    isLoading,
    categories,
    fetchMySettings,
    setMyValue,
    resetMyValue,
  };
};
